import React from 'react';
import {
    Box, TextField, IconButton, Typography,
    Avatar, Collapse, useMediaQuery, useTheme
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Close as CloseIcon, GroupAdd as GroupAddIcon } from '@mui/icons-material';
import { TransitionGroup } from 'react-transition-group';

const ParticipantsInput = ({ value = [], onChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleAddParticipant = () => {
        onChange([...value, { name: '', context: '' }]);
    };

    const handleRemoveParticipant = (index) => {
        const updated = value.filter((_, i) => i !== index);
        onChange(updated);
    };

    const handleParticipantChange = (index, field, newValue) => {
        const updated = value.map((participant, i) => {
            if (i === index) {
                return { ...participant, [field]: newValue };
            }
            return participant;
        });
        onChange(updated);
    };

    return (
        <Box sx={{ mt: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                    Participants (optional)
                </Typography>
            </Box>

            {/* Participant cards */}
            {value.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TransitionGroup component={null}>
                        {value.map((participant, index) => (
                            <Collapse key={index} timeout={250}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        alignItems: 'center',
                                        p: 1.5,
                                        mb: index < value.length - 1 ? 1.5 : 0,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        backgroundColor: 'action.hover',
                                        transition: 'border-color 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            boxShadow: `0 0 0 1px ${theme.palette.primary.main}22`,
                                        },
                                        '&:hover .delete-btn': {
                                            opacity: 1,
                                        }
                                    }}
                                >
                                    {/* Number avatar */}
                                    <Avatar
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            flexShrink: 0,
                                            alignSelf: isMobile ? 'flex-start' : 'center',
                                            mt: isMobile ? 0.5 : 0,
                                        }}
                                    >
                                        {index + 1}
                                    </Avatar>

                                    {/* Fields */}
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        gap: 1,
                                        minWidth: 0,
                                    }}>
                                        <TextField
                                            label="Name"
                                            value={participant.name}
                                            onChange={(e) => handleParticipantChange(index, 'name', e.target.value)}
                                            size="small"
                                            fullWidth
                                            placeholder="e.g., John Smith"
                                            sx={{
                                                flex: isMobile ? 1 : '0 0 35%',
                                                '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
                                                '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                                            }}
                                        />
                                        <TextField
                                            label="Context"
                                            value={participant.context}
                                            onChange={(e) => handleParticipantChange(index, 'context', e.target.value)}
                                            size="small"
                                            fullWidth
                                            placeholder="e.g., Project Manager, leading the discussion"
                                            sx={{
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
                                                '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                                            }}
                                        />
                                    </Box>

                                    {/* Delete button - subtle, visible on hover (always visible on mobile) */}
                                    <IconButton
                                        className="delete-btn"
                                        onClick={() => handleRemoveParticipant(index)}
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            opacity: isMobile ? 0.7 : 0.3,
                                            transition: 'opacity 0.2s, color 0.2s, background-color 0.2s',
                                            alignSelf: isMobile ? 'flex-start' : 'center',
                                            mt: isMobile ? 0.5 : 0,
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'error.main',
                                                backgroundColor: 'error.main',
                                                // 12% opacity background
                                                bgcolor: theme => `${theme.palette.error.main}14`,
                                            }
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: '1.1rem' }} />
                                    </IconButton>
                                </Box>
                            </Collapse>
                        ))}
                    </TransitionGroup>

                    {/* Add another - always at the bottom of the list */}
                    <Box
                        onClick={handleAddParticipant}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            mt: 0.5,
                            py: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 2,
                            cursor: 'pointer',
                            transition: 'border-color 0.2s, background-color 0.2s',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover',
                            }
                        }}
                    >
                        <PersonAddIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                            Add participant
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Empty state / Add first participant */}
            {value.length === 0 && (
                <Box
                    onClick={handleAddParticipant}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 2.5,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover',
                        }
                    }}
                >
                    <GroupAddIcon sx={{ fontSize: '1.8rem', color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Add participants to provide context for the summarization
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                        + Add participant
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ParticipantsInput;
